import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-server',
    title: '1. Legacy Application Modernization',
    desc: 'Refactor, containerize, and modernize mission-critical COBOL, PL/I, and CICS legacy applications without disrupting core business operations.',
  },
  {
    icon: 'fa-solid fa-cloud-arrow-up',
    title: '2. Mainframe-to-Cloud Integration',
    desc: 'Expose mainframe assets securely via RESTful APIs and z/OS Connect to seamlessly interface with modern cloud and microservice architectures.',
  },
  {
    icon: 'fa-solid fa-microchip',
    title: '3. z/OS Systems & Infrastructure Management',
    desc: 'Comprehensive administration and maintenance of z/OS, z/VM, and z/Linux systems to ensure continuous availability and system health.',
  },
  {
    icon: 'fa-solid fa-bolt',
    title: '4. High-Throughput Transaction Processing',
    desc: 'Optimize CICS, IMS, and batch JCL workflows to handle millions of daily high-concurrency transactions with ultra-low latency.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '5. Mainframe Security & RACF Governance',
    desc: 'Protect enterprise core systems using RACF/ACF2 access policies, dataset-level auditing, and hardware-accelerated pervasive encryption.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '6. Db2 & VSAM Data Optimization',
    desc: 'Tune relational Db2 for z/OS databases and legacy VSAM datasets to optimize query performance and reduce operational overhead.',
  },
  {
    icon: 'fa-solid fa-gauge-high',
    title: '7. MIPS & Capacity Optimization',
    desc: 'Analyze SMF/RMF metrics to tune CPU usage, trim MIPS/MSU consumption, and significantly reduce monthly software license fees.',
  },
  {
    icon: 'fa-solid fa-rotate',
    title: '8. Automated Mainframe DevSecOps',
    desc: 'Implement modern CI/CD pipelines using Git, Jenkins, and IBM Z Open Development for faster, safer mainframe software release cycles.',
  },
  {
    icon: 'fa-solid fa-arrows-spin',
    title: '9. Replatforming & Cloud Migration',
    desc: 'Migrate workloads from legacy mainframe hardware to cloud environments (AWS, Azure, GCP) via automated code translation and rehosting.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '10. Disaster Recovery & High Availability',
    desc: 'Architect Parallel Sysplex and GDPS configurations to deliver $99.999\%$ uptime with automated zero-data-loss failover capability.',
  },
];

const whyChooseUs = [
  'Guaranteed 99.999% availability and continuous uptime',
  'Deep domain expertise in COBOL, PL/I, JCL, and z/OS',
  'MIPS & MSU cost reduction and capacity tuning',
  'Hardware-level pervasive encryption & RACF security',
  'Seamless hybrid integration via z/OS Connect APIs',
  '24/7/365 dedicated mainframe monitoring and administration',
  'Automated DevSecOps pipelines for legacy codebases',
  'Parallel Sysplex multi-node high availability',
  'Proven legacy-to-cloud migration methodologies',
  'Zero-downtime system upgrades and patching',
];

const processSteps = [
  { step: '01', title: 'Mainframe Discovery & Audit', desc: 'Assess existing legacy codebases, MIPS utilization, security policies, and dependencies.' },
  { step: '02', title: 'Modernization Strategy', desc: 'Design tailored roadmaps for refactoring, API integration, or cloud migration.' },
  { step: '03', title: 'Code & Pipeline Refactoring', desc: 'Optimize COBOL/JCL batch workflows, update databases, and set up CI/CD pipelines.' },
  { step: '04', title: 'Security Hardening', desc: 'Enforce pervasive encryption, refine RACF access rules, and ensure SOC2/HIPAA compliance.' },
  { step: '05', title: 'Stress Testing & Sysplex Cutover', desc: 'Perform heavy transaction load testing and seamless cutover with zero downtime.' },
  { step: '06', title: '24/7 Managed Operations', desc: 'Deliver continuous telemetry monitoring, capacity planning, and incident management.' },
];

const techStack = [
  { category: 'Operating Systems & Hardware', items: ['IBM z/OS', 'z/VM', 'z/VSE', 'Linux on IBM Z', 'IBM zSystems'] },
  { category: 'Languages & Subsystems', items: ['COBOL', 'PL/I', 'CICS', 'IMS', 'JCL', 'REXX'] },
  { category: 'Databases & Storage', items: ['IBM Db2 for z/OS', 'VSAM', 'Adabas', 'IMS DB'] },
  { category: 'DevOps & Cloud Integration', items: ['z/OS Connect EE', 'IBM Z Open Dev', 'Git', 'Jenkins', 'UrbanCode'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const MainframeComputing = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Enterprise Infrastructure
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Mainframe <span className="gradient-text-cyan">Computing</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we modernize, secure, and manage mission-critical mainframe environments. Bridge legacy enterprise infrastructure with modern cloud architectures while maintaining unyielding uptime, extreme transaction performance, and hardware-grade security.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Modernize Your Mainframe With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Mainframe Computing Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Mainframe Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Mainframe Delivery Model</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Optimize & Modernize Your Mainframe with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your legacy infrastructure into a agile, high-performance power station with our expert mainframe engineering team.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Mainframe Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MainframeComputing;